import { useGlobalAPIQueryContext } from "@dev2_techwalnut/tw-api-query-hook";
import { useAPIQuery } from "../../../hooks/useAPIQuery";
import { zodResolver } from "@hookform/resolvers/zod";
import moment from "moment";
import { useEffect, useRef } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import secureLocalStorage from "react-secure-storage";
import Select from "react-select";
import Swal from "sweetalert2";
import { z } from "zod";

const createSchema = (targetQuantity) => {
    return z.object({
        work_orders: z
            .array(
                z
                    .object({
                        work_order_id: z.object(
                            {
                                value: z.union([z.string(), z.number()]),
                                label: z.string(),
                                available_stock: z.number().optional(),
                                multiplication_factor: z.number().optional(),
                            },
                            { message: "Work order is required" }
                        ),
                        allocated_quantity: z
                            .number({
                                required_error: "Alloting quantity is required",
                                invalid_type_error:
                                    "Alloting quantity is required",
                            })
                            .min(0, `Alloting quantity cannot be negative`),
                        allocated_quantity_actual: z
                            .number({
                                required_error: "Alloting quantity is required",
                                invalid_type_error:
                                    "Alloting quantity is required",
                            })
                            .min(0, `Alloting quantity cannot be negative`),
                    })
                    .superRefine((workOrder, ctx) => {
                        const availableStock =
                            workOrder.work_order_id?.available_stock;
                        const allocatedQty =
                            workOrder.allocated_quantity_actual;

                        if (
                            availableStock !== undefined &&
                            allocatedQty > availableStock
                        ) {
                            ctx.addIssue({
                                code: z.ZodIssueCode.custom,
                                message: `Allocated quantity (${allocatedQty}) cannot exceed available stock (${availableStock})`,
                                path: ["allocated_quantity"],
                            });
                        }
                    })
            )
            .superRefine((workOrders, ctx) => {
                // Validate total allocated quantity against target quantity
                const total = workOrders.reduce(
                    (sum, wo) => sum + (wo.allocated_quantity_actual || 0),
                    0
                );

                if (total > targetQuantity) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `Alloted quantity (${total}) cannot exceed requested quantity: (${targetQuantity}).`,
                        path: ["allocated_quantity"],
                    });
                }
            }),
    });
};

const AllotFG = ({ disabled, targetQuantity, salesOrderId, itemId }) => {
    const userID = secureLocalStorage.getItem("userID");
    const {
        register,
        handleSubmit,
        formState: { errors },
        control,
        setValue,
        watch,
        trigger,
    } = useForm({
        mode: "onBlur",
        resolver: zodResolver(createSchema(targetQuantity ?? 0)),
    });

    const closeButtonRef = useRef(null);

    const handleCloseModal = () => {
        if (closeButtonRef.current) {
            closeButtonRef.current.click();
        }
    };

    const { useFetch: useFetchBatches } = useAPIQuery({
        key: "api/batch",
        endpoint: "api/batch",
    });
    const { data: batches, isLoading: loadingBatches } = useFetchBatches({
        params: {
            items: itemId,
        },
        abortOnNewRequest: true,
        refetchOnWindowFocus: false,
        staleTime: 1000 * 60 * 3,
        retry: 3,
        keepPlaceholder: true,
        refetchOnReconnect: true,
        enabled: !!itemId,
    });

    const { fields, append, remove } = useFieldArray({
        name: "work_orders",
        control,
    });

    const watchedBatches = watch(`work_orders`);

    const total_alloted_quantity = watch("work_orders")?.reduce(
        (sum, wo) => sum + (wo?.allocated_quantity_actual || 0),
        0
    );

    const { invalidateApi } = useGlobalAPIQueryContext();

    const { useUpdate: updateSalesOrder } = useAPIQuery({
        key: `sales_order_for_${salesOrderId}`,
        endpoint: `/api/sales-order/${salesOrderId}/`,
    });

    const { mutate: mutateSalesOrder, isPending: allotingFG } =
        updateSalesOrder({
            onSuccess: () => {
                invalidateApi({ key: `api/sales-order/${salesOrderId}` });
                invalidateApi({ key: `api/work-order` });
                handleCloseModal();
            },
            successMessageFromServer: true,
            errorMessageFromServer: true,
        });

    const dontAllocate = () => {
        Swal.fire({
            text: `Are you sure? you don't want to allott any Finished Good?`,
            showDenyButton: true,
            confirmButtonText: "Yes",
            denyButtonText: `No, cancel!`,
        }).then((result) => {
            if (result.isConfirmed) {
                mutateSalesOrder({
                    data: {
                        stock_allotted: 0,
                        is_stock_allotted: true,
                        updated_by: userID,
                    },
                });
            }
        });
    };

    const onSubmit = (data) => {
        console.log(data);
        const total = data?.work_orders?.reduce(
            (sum, wo) => sum + (wo?.allocated_quantity_actual || 0),
            0
        );
        // const actualTotal = data?.work_orders?.reduce(
        //     (sum, wo) =>
        //         sum +
        //         (Math.floor(
        //             wo?.allocated_quantity /
        //                 (wo?.work_order_id?.multiplication_factor ?? 1)
        //         ) || 0),
        //     0
        // );
        Swal.fire({
            text: `Would you like to allott ${total || 0} Finished Good?`,
            showDenyButton: true,
            confirmButtonText: "Yes",
            denyButtonText: `No, cancel!`,
        }).then((result) => {
            if (result.isConfirmed) {
                console.log(data);
                const res = {
                    batches: data?.work_orders?.map((wo) => ({
                        batch_no: wo?.work_order_id?.value,
                        received_quantity: wo?.allocated_quantity_actual,
                    })),
                    stock_allotted: Number(total),
                    is_stock_allotted: true,
                    updated_by: userID,
                };
                console.log(res);
                mutateSalesOrder({
                    data: res,
                });
            }
        });
    };

    useEffect(() => {
        return () => {
            const backdrop = document.querySelector(".modal-backdrop");
            if (backdrop) {
                backdrop.remove();
                document.body.classList.remove("modal-open");
                document.body.style = "";
            }
        };
    }, []);

    return (
        <>
            <div
                className="modal fade m-0 p-0"
                tabIndex={-1}
                id={`allocate-fg`}
                aria-modal
            >
                <div className="modal-dialog modal-lg" role="document">
                    <div className="modal-content">
                        <button
                            type="button"
                            className="close"
                            data-bs-dismiss="modal"
                            ref={closeButtonRef}
                        >
                            <em
                                className="icon ni ni-cross"
                                aria-hidden="true"
                            ></em>
                        </button>
                        <div className="modal-header">
                            <h5 className="modal-title">Allocate FG</h5>
                        </div>
                        <form onSubmit={handleSubmit(onSubmit)}>
                            <div className="modal-body">
                                {errors?.work_orders?.allocated_quantity && (
                                    <div className="row g-3 mb-3">
                                        <div className="col-12">
                                            <p className="text-danger">
                                                {
                                                    errors?.work_orders
                                                        ?.allocated_quantity
                                                        ?.message
                                                }
                                            </p>
                                        </div>
                                    </div>
                                )}
                                <div className="row g-3 mb-3">
                                    <div className="col-4">
                                        <p>
                                            <strong className="me-2">
                                                Target Quantity:
                                            </strong>
                                            {targetQuantity}
                                        </p>
                                    </div>
                                    <div className="col-4">
                                        <p>
                                            <strong className="me-2">
                                                Alloted Quantity:
                                            </strong>
                                            {total_alloted_quantity}
                                        </p>
                                    </div>
                                </div>
                                {fields?.length > 0 ? (
                                    <div className="table-responsive">
                                        <table className="table table-bordered mb-3">
                                            <thead>
                                                <tr>
                                                    <th
                                                        style={{ width: "40%" }}
                                                    >
                                                        Work Order
                                                        <small className="text-danger ms-1">
                                                            *
                                                        </small>
                                                    </th>
                                                    <th
                                                        style={{ width: "40%" }}
                                                    >
                                                        Quantity
                                                        <small className="text-danger ms-1">
                                                            *
                                                        </small>
                                                    </th>
                                                    <th
                                                        style={{ width: "20%" }}
                                                    >
                                                        Remove
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {fields?.map((field, i) => (
                                                    <WorkOrderRow
                                                        key={field.id}
                                                        field={field}
                                                        i={i}
                                                        control={control}
                                                        register={register}
                                                        watch={watch}
                                                        setValue={setValue}
                                                        remove={remove}
                                                        errors={errors}
                                                        batches={batches}
                                                        loadingBatches={
                                                            loadingBatches
                                                        }
                                                        watchedBatches={
                                                            watchedBatches
                                                        }
                                                    />
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : null}
                                <button
                                    type="button"
                                    className="btn btn-sm btn-primary"
                                    onClick={() =>
                                        append({
                                            work_order_id: null,
                                            allocated_quantity: 0,
                                            allocated_quantity_actual: 0,
                                        })
                                    }
                                >
                                    + Allocate
                                </button>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={() => dontAllocate()}
                                    disabled={allotingFG}
                                >
                                    Don't Allocate
                                </button>
                                <button
                                    type="sumbit"
                                    className="btn btn-sm btn-outline-primary"
                                    disabled={allotingFG || fields?.length <= 0}
                                    onClick={() => trigger()}
                                >
                                    {allotingFG ? "Submitting..." : "Submit"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
            <button
                type="submit"
                className="btn btn-sm btn-outline-primary"
                data-bs-toggle="modal"
                data-bs-target="#allocate-fg"
                disabled={disabled}
            >
                {allotingFG ? "Loading..." : "Allot"}
            </button>
        </>
    );
};

const WorkOrderRow = ({
    field,
    i,
    control,
    register,
    watch,
    setValue,
    remove,
    errors,
    batches,
    loadingBatches,
    watchedBatches,
}) => {
    const allocatedQuantity = watch(`work_orders.${i}.allocated_quantity`);
    const multiplicationFactor = watch(
        `work_orders.${i}.work_order_id.multiplication_factor`
    );

    useEffect(() => {
        if (allocatedQuantity && multiplicationFactor) {
            const total_required = Math.ceil(
                Number(allocatedQuantity) * multiplicationFactor
            );
            const total_quantity = Math.round(
                total_required / multiplicationFactor
            );

            setValue(
                `work_orders.${i}.allocated_quantity_actual`,
                total_quantity,
                {
                    shouldValidate: true,
                    shouldDirty: true,
                }
            );
        }
    }, [allocatedQuantity, multiplicationFactor, i, setValue]);

    return (
        <tr key={field.id}>
            {/* -------- Work Order Select -------- */}
            <td>
                <Controller
                    name={`work_orders.${i}.work_order_id`}
                    control={control}
                    render={({ field }) => {
                        const selectedBatches = watchedBatches
                            .filter((_, idx) => idx !== i)
                            .map((f) => f.work_order_id?.label)
                            .filter(Boolean);

                        const availableOptions =
                            Object.values(batches?.results?.data ?? {})
                                ?.flat()
                                ?.map((b) => ({
                                    multiplication_factor:
                                        b?.multiplication_factor,
                                    available_stock: Math.round(
                                        b?.received_quantity /
                                            (b?.multiplication_factor ?? 1)
                                    ),
                                    value: b?.batch_number,
                                    label: `${b?.batch_number}${
                                        b?.created_at &&
                                        moment(b?.created_at).isValid()
                                            ? ` : ${moment(b.created_at).format(
                                                  "DD-MM-YYYY"
                                              )}`
                                            : ""
                                    } : Qty ${Math.round(
                                        b?.received_quantity /
                                            (b?.multiplication_factor ?? 1)
                                    )}`,
                                }))
                                ?.filter(
                                    (option) =>
                                        !selectedBatches.includes(option.label)
                                ) || [];

                        return (
                            <Select
                                {...field}
                                options={availableOptions}
                                isLoading={loadingBatches}
                                styles={{
                                    menuPortal: (base) => ({
                                        ...base,
                                        zIndex: 9999,
                                    }),
                                    menu: (base) => ({
                                        ...base,
                                        zIndex: 9999,
                                    }),
                                }}
                                menuPortalTarget={document.body}
                                placeholder={`Select a work order`}
                                onChange={(val) => {
                                    field.onChange(val);
                                    setValue(
                                        `work_orders.${i}.allocated_quantity`,
                                        val?.available_stock || 0,
                                        {
                                            shouldValidate: true,
                                            shouldDirty: true,
                                        }
                                    );
                                    setValue(
                                        `work_orders.${i}.allocated_quantity_actual`,
                                        val?.available_stock || 0,
                                        {
                                            shouldValidate: true,
                                            shouldDirty: true,
                                        }
                                    );
                                }}
                            />
                        );
                    }}
                />
                {errors?.work_orders?.[i]?.work_order_id?.message && (
                    <span className="fs-8 text-danger">
                        {errors.work_orders[i].work_order_id.message}
                    </span>
                )}
            </td>

            {/* -------- Quantity Inputs -------- */}
            <td>
                <div className="row">
                    <div className="col-5">
                        <input
                            type="number"
                            className="form-control"
                            {...register(
                                `work_orders.${i}.allocated_quantity`,
                                {
                                    valueAsNumber: true,
                                }
                            )}
                            disabled={!watch(`work_orders.${i}.work_order_id`)}
                        />
                    </div>

                    <div className="col-1 align-self-center text-center">
                        <em className="icon ni ni-arrow-long-right"></em>
                    </div>

                    <div className="col-5">
                        <input
                            type="number"
                            className="form-control"
                            {...register(
                                `work_orders.${i}.allocated_quantity_actual`,
                                {
                                    valueAsNumber: true,
                                }
                            )}
                            disabled={!watch(`work_orders.${i}.work_order_id`)}
                            readOnly
                        />
                    </div>
                </div>

                {errors?.work_orders?.[i]?.allocated_quantity?.message && (
                    <span className="fs-8 text-danger">
                        {errors.work_orders[i].allocated_quantity.message}
                    </span>
                )}
            </td>

            {/* -------- Remove Button -------- */}
            <td>
                <button
                    type="button"
                    className="btn btn-sm btn-danger"
                    onClick={() => remove(i)}
                >
                    Remove
                </button>
            </td>
        </tr>
    );
};

export default AllotFG;
